'use client';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';
import { InitialsBadge } from '../DataTableCells';

export type CommitteeVoteState = 'APPROVED' | 'REJECTED' | 'PENDING' | 'ABSTAINED';

export interface CommitteeVoteTileProps {
  /** Voter display name. */
  name: ReactNode;
  /** Voter role (e.g. "CCO", "Risk Committee Chair"). */
  role: ReactNode;
  /** Voter initials for the badge (max 3 chars). Falls back to first letter of name. */
  initials?: string;
  /** Optional avatar image URL. If absent, InitialsBadge is shown. */
  avatarUrl?: string;
  /** Vote state. */
  state: CommitteeVoteState;
  /** Optional justification text. Truncated if long; full text via tooltip on hover. */
  justification?: ReactNode;
  /** Optional handler — clicking the tile (used in queue overviews). */
  onClick?: () => void;
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

const STATE_META: Record<
  CommitteeVoteState,
  {
    label: string;
    palette: 'success' | 'error' | 'warning' | 'neutral';
    icon: typeof CheckCircleIcon;
  }
> = {
  APPROVED: { label: 'Approved', palette: 'success', icon: CheckCircleIcon },
  REJECTED: { label: 'Rejected', palette: 'error', icon: CloseIcon },
  PENDING: { label: 'Pending', palette: 'warning', icon: ScheduleIcon },
  ABSTAINED: { label: 'Abstained', palette: 'neutral', icon: RemoveIcon },
};

function paletteColor(theme: Theme, palette: 'success' | 'error' | 'warning' | 'neutral') {
  if (palette === 'neutral') return theme.palette.text.secondary;
  return theme.palette[palette].main;
}

export function CommitteeVoteTile({
  name,
  role,
  initials,
  avatarUrl,
  state,
  justification,
  onClick,
  sx,
}: CommitteeVoteTileProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const meta = STATE_META[state];
  const color = paletteColor(theme, meta.palette);
  const StateIcon = meta.icon;
  const interactive = Boolean(onClick);

  // Initials fallback: first letter of name if not provided.
  const resolvedInitials =
    initials ??
    (typeof name === 'string'
      ? name
          .split(/\s+/)
          .map((w) => w[0])
          .filter(Boolean)
          .slice(0, 2)
          .join('')
      : '?');

  return (
    <Box
      component={interactive ? 'button' : 'div'}
      {...(interactive ? { type: 'button' as const, onClick } : {})}
      sx={composeSx(
        {
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 1.5,
          minWidth: 160,
          borderRadius: 1.5,
          border: `1px solid ${alpha(color, isDark ? 0.32 : 0.24)}`,
          backgroundColor: alpha(color, isDark ? 0.08 : 0.06),
          textAlign: 'left',
          fontFamily: 'inherit',
          cursor: interactive ? 'pointer' : 'default',
          transition: 'background-color 120ms ease, border-color 120ms ease',
          ...(interactive && {
            '&:hover': {
              backgroundColor: alpha(color, isDark ? 0.12 : 0.1),
            },
            '&:focus-visible': {
              outline: `2px solid ${color}`,
              outlineOffset: 2,
            },
          }),
        },
        sx,
      )}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        {avatarUrl ? (
          <Box
            component="img"
            src={avatarUrl}
            alt=""
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <InitialsBadge initials={resolvedInitials} size="md" colorSeed={String(name)} />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {role}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color }}>
        <StateIcon sx={{ fontSize: 16 }} />
        <Typography
          component="span"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'inherit',
            letterSpacing: '0.02em',
          }}
        >
          {meta.label}
        </Typography>
      </Box>

      {justification && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {justification}
        </Typography>
      )}
    </Box>
  );
}

CommitteeVoteTile.displayName = 'CommitteeVoteTile';

export default CommitteeVoteTile;
